import { useState, useContext, useRef, useEffect } from 'react';
import { Box, Typography, Paper, Button, CircularProgress, IconButton, Alert, FormControlLabel, Checkbox, Divider, Chip } from '@mui/material';
import { ThemeContext } from '../Context';
import { GuardiaAccessChecker } from './GuardiaAccessChecker';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { ReactComponent as GuardiaLogo } from '../../assets/logo_guardia.svg';
import { logAction, LOG_ACTIONS } from '../../utils/logClient';

function GuardiaAudio() {
  const { theme } = useContext(ThemeContext);

  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';

  // États pour gérer l'audio et la réponse
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [transcription, setTranscription] = useState('');
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [guardiaAccess, setGuardiaAccess] = useState({ hasAccess: false, reason: '' });
  const [resumeAccess, setResumeAccess] = useState({ hasAccess: false, reason: '' });
  const [accessChecked, setAccessChecked] = useState(false);
  const fileInputRef = useRef(null);

  // États pour les options d'affichage
  const [showSegments, setShowSegments] = useState(false); // false = texte complet, true = par segments avec timecodes

  // États pour l'analyse GuardIA
  const [summary, setSummary] = useState('');
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Vérifier l'accès GuardIA au chargement
  useEffect(() => {
    const checkAccess = async () => {
      const [audioAccess, summarizeAccess] = await Promise.all([
        GuardiaAccessChecker.checkFeatureAccess('audio'),
        GuardiaAccessChecker.checkFeatureAccess('resume')
      ]);
      setGuardiaAccess(audioAccess);
      setResumeAccess(summarizeAccess);
      setAccessChecked(true);
    };
    
    checkAccess();
  }, []);

  // Gestionnaire pour l'upload d'audio
  const handleAudioUpload = (file) => {
    // Vider les erreurs précédentes
    setError('');

    if (!file) return;

    // Vérifier la taille du fichier (25 Mo maximum)
    const maxSize = 25 * 1024 * 1024; // 25 Mo en octets
    if (file.size > maxSize) {
      setError(`Le fichier est trop volumineux. La taille maximale autorisée est de 25 Mo. Taille actuelle : ${Math.round(file.size / (1024 * 1024))} Mo.`);
      return;
    }

    // Vérifier le format du fichier
    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/mp4', 'audio/m4a', 'audio/webm'];
    const allowedExtensions = ['.mp3', '.wav', '.m4a', '.mp4', '.webm'];
    const fileName = file.name.toLowerCase();

    const isValidType = allowedTypes.includes(file.type);
    const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!isValidType && !isValidExtension) {
      setError('Format de fichier non supporté. Formats acceptés : MP3, WAV, M4A, MP4, WebM.');
      return;
    }

    // Si tout est valide
    setSelectedAudio(file);
    const reader = new FileReader();
    reader.onload = (e) => setAudioPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  // Gestionnaire pour le drop
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleAudioUpload(files[0]);
    }
  };

  // Gestionnaire pour le drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  // Gestionnaire pour le drag leave
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  // Gestionnaire pour le clic sur parcourir
  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  // Gestionnaire pour la sélection de fichier
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleAudioUpload(file);
    }
  };

  // Gestionnaire pour l'envoi
  const handleSend = async () => {
    if (!guardiaAccess.hasAccess) {
      setError(guardiaAccess.reason);
      return;
    }
    
    if (!selectedAudio) return;

    setLoading(true);
    setError('');
    setTranscription('');
    setSegments([]);
    setSummary('');

    try {
      // Créer un FormData pour envoyer le fichier audio
      const formData = new FormData();
      formData.append('audio', selectedAudio);

      // Appeler l'API GuardIA pour la transcription audio
      const token = sessionStorage.getItem('authToken');
      const response = await fetch('/api/guardia/transcribe', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setTranscription(data.transcription);
        setSegments(data.segments || []);

        // Log de la transcription audio
        logAction(LOG_ACTIONS.GUARDIA_AUDIO, null, {
          action: 'transcription',
          fileName: selectedAudio?.name || null,
          transcriptionLength: data.transcription?.length || 0,
          transcription: data.transcription || ''
        });
      } else {
        setError(data.message || 'Erreur lors de la transcription de l\'audio');
      }
    } catch (error) {
      console.error('Erreur lors de l\'appel à l\'API:', error);
      setError('Erreur de connexion au service GuardIA. Vérifiez que le serveur backend est démarré.');
    } finally {
      setLoading(false);
    }
  };

  // Gestionnaire pour nouveau fichier audio
  const handleNewAudio = () => {
    setSelectedAudio(null);
    setAudioPreview(null);
    setTranscription('');
    setSegments([]);
    setSummary('');
    setLoading(false);
    setLoadingSummary(false);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Gestionnaire pour résumer avec GuardIA
  const handleSummarize = async () => {
    if (!transcription || !resumeAccess.hasAccess) {
      if (!resumeAccess.hasAccess) {
        setError(resumeAccess.reason);
      }
      return;
    }

    setLoadingSummary(true);
    setError('');

    try {
      const token = sessionStorage.getItem('authToken');
      const response = await fetch('/api/guardia/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: transcription,
          profile: 'GuardiaResume'
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSummary(data.response);

        // Log du résumé audio
        logAction(LOG_ACTIONS.GUARDIA_AUDIO, null, {
          action: 'resume',
          summaryLength: data.response?.length || 0,
          summary: data.response || ''
        });
      } else {
        setError(data.message || 'Erreur lors de la génération du résumé');
      }
    } catch (error) {
      console.error('Erreur lors de l\'appel à l\'API GuardIA:', error);
      setError('Erreur de connexion au service GuardIA pour le résumé.');
    } finally {
      setLoadingSummary(false);
    }
  };

  // Gestionnaire pour copier la transcription
  const handleCopyTranscription = async () => {
    if (!transcription) return;

    try {
      let textToCopy = transcription;
      
      // Si l'affichage par segments est activé, copier avec les timecodes
      if (showSegments && segments.length > 0) {
        textToCopy = segments
          .map(segment => `[${segment.timecode}] ${segment.text}`)
          .join('\n');
      }
      
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      // Reset l'état "copié" après 2 secondes
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

  // Si l'accès n'est pas encore vérifié, afficher un loader
  if (!accessChecked) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDark ? '#1a252f' : '#f8f9fa',
        }}
      >
        <CircularProgress sx={{ color: isDark ? '#4fc3f7' : 'primary.main' }} />
      </Box>
    );
  }

  // Si l'accès n'est pas autorisé, afficher un message d'erreur
  if (accessChecked && !guardiaAccess.hasAccess) {
    return (
      <Box 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDark ? '#1a252f' : '#f5f5f5',
          p: 3
        }}
      >
        <Alert 
          severity="warning" 
          sx={{
            backgroundColor: isDark ? 'rgba(255, 193, 7, 0.15)' : undefined,
            color: isDark ? '#ffc107' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#ffc107' : undefined
            },
            maxWidth: 400,
            textAlign: 'center'
          }}
        >
          {guardiaAccess.reason}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        backgroundColor: isDark ? '#1a252f' : '#f8f9fa',
        p: 1.5,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
          borderRadius: '10px',
          '&:hover': {
            backgroundColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)',
          },
        },
      }}
    >
      {/* Section supérieure - Upload et contrôles */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: isDark ? 'rgba(52, 73, 94, 0.7)' : 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${isDark ? 'rgba(79, 195, 247, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
            width: '100%',
            maxWidth: '95%',
          }}
        >

          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 500,
              color: isDark ? '#ecf0f1' : 'text.primary',
              mb: 1
            }}
          >
            <strong>GuardIA</strong> vous aide à transcrire des fichiers audio.
          </Typography>

          {/* Zone d'upload d'audio */}
          {!selectedAudio ? (
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={{
                border: `2px dashed ${dragOver
                  ? (theme === 'dark' ? '#c084fc' : '#800080')
                  : (isDark ? 'rgba(79, 195, 247, 0.3)' : 'rgba(25, 118, 210, 0.3)')}`,
                borderRadius: 2,
                p: 1.5,
                mb: 1.5,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backgroundColor: dragOver
                  ? (isDark ? '#c084fc' : '#800080')
                  : 'transparent',
                '&:hover': {
                  borderColor: theme === 'dark' ? '#c084fc' : '#800080',
                  backgroundColor: isDark ? 'rgba(192, 132, 252, 0.05)' : 'rgba(128, 0, 128, 0.05)',
                },
              }}
              onClick={handleBrowseClick}
            >
              <AudioFileIcon
                sx={{
                  fontSize: 24,
                  color: isDark ? '#c084fc' : '#800080',
                  mb: 0.5,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? '#ecf0f1' : 'text.primary',
                  mb: 0.25,
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  lineHeight: 1.2,
                }}
              >
                Déposez un fichier audio ici ou cliquez pour parcourir
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? '#bdc3c7' : 'text.secondary',
                  fontSize: '0.7rem',
                  lineHeight: 1.2,
                }}
              >
                Formats acceptés : MP3, WAV, M4A, MP4, WebM (max 25 Mo)
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </Box>
          ) : (
            /* Preview de l'audio */
            <Box sx={{ mb: 0.5 }}>
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: 1,
                  p: 1,
                  borderRadius: 2,
                  backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
                }}
              >
                <AudioFileIcon 
                  sx={{ 
                    fontSize: 48, 
                    color: isDark ? '#c084fc' : '#800080',
                    mb: 0,
                  }} 
                />
                {audioPreview && (
                  <audio 
                    controls 
                    src={audioPreview}
                    style={{ 
                      width: '100%', 
                      maxWidth: 300,
                      marginBottom: 1,
                    }}
                  />
                )}
                <Button
                  size="small"
                  onClick={handleNewAudio}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    minWidth: 'auto',
                    width: 28,
                    height: 28,
                    padding: 0,
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.8)',
                    },
                  }}
                >
                  ✕
                </Button>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? '#bdc3c7' : 'text.secondary',
                  mb: 0.5,
                  fontSize: '0.7rem',
                }}
              >
                {selectedAudio.name} ({Math.round(selectedAudio.size / 1024)} KB)
              </Typography>
            </Box>
          )}

          {/* Bouton Envoyer */}
          {selectedAudio && (
            <Box sx={{ mb: 1 }}>
              <Button
                variant="contained"
                onClick={handleSend}
                disabled={!guardiaAccess.hasAccess || loading}
                sx={{
                  backgroundColor: theme === 'dark' ? '#c084fc' : '#800080',
                  color: 'white',
                  px: 2.5,
                  py: 0.75,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  '&:hover': {
                    backgroundColor: theme === 'dark' ? '#a855f7' : '#6b0050',
                  },
                  '&:disabled': {
                    backgroundColor: isDark ? '#2d3f4c' : 'grey.300',
                    color: isDark ? '#7f8c8d' : 'grey.500',
                  },
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Transcription en cours...
                  </>
                ) : (
                  'Transcrire'
                )}
              </Button>
            </Box>
          )}

          {/* Bouton Nouveau fichier (quand un fichier est sélectionné) */}
          {selectedAudio && !loading && (
            <Button
              size="small"
              onClick={handleNewAudio}
              sx={{
                color: isDark ? '#95a5a6' : 'text.secondary',
                fontSize: '0.7rem',
                px: 1,
                py: 0.25,
                minHeight: 'auto',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                },
              }}
            >
              Nouveau fichier
            </Button>
          )}

          {/* Message d'erreur */}
          {error && (
            <Box
              sx={{
                mt: 1.5,
                p: 1,
                backgroundColor: isDark ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)',
                border: `1px solid ${isDark ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.2)'}`,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#f44336',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                }}
              >
                {error}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Section transcription - avec options et détails */}
      {transcription && (
        <Box sx={{ mt: 1.5, px: 1.5, pb: 2 }}>
          {/* Option d'affichage */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 1.5,
              backgroundColor: isDark ? 'rgba(52, 73, 94, 0.5)' : 'rgba(255,255,255,0.5)',
              borderRadius: 2,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={showSegments}
                  onChange={(e) => setShowSegments(e.target.checked)}
                  size="small"
                  sx={{
                    color: isDark ? '#95a5a6' : 'text.secondary',
                    '&.Mui-checked': {
                      color: theme === 'dark' ? '#c084fc' : '#800080',
                    },
                  }}
                />
              }
              label="Afficher par segments avec timecodes"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.8rem',
                  color: isDark ? '#bdc3c7' : 'text.secondary',
                },
              }}
            />
          </Paper>

          {/* Transcription */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 1.5,
              backgroundColor: isDark ? 'rgba(52, 73, 94, 0.7)' : 'rgba(255,255,255,0.7)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${isDark ? 'rgba(79, 195, 247, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: isDark ? '#ecf0f1' : 'text.primary',
                  fontSize: '0.85rem',
                }}
              >
                Transcription
              </Typography>

              <IconButton
                onClick={handleCopyTranscription}
                size="small"
                sx={{
                  color: copied
                    ? (isDark ? '#4ade80' : '#22c55e')
                    : (isDark ? '#95a5a6' : 'text.secondary'),
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  },
                  transition: 'color 0.2s ease',
                }}
                title={copied ? 'Copié !' : 'Copier la transcription'}
              >
                {copied ? (
                  <CheckIcon sx={{ fontSize: '1rem' }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: '1rem' }} />
                )}
              </IconButton>
            </Box>

            {!showSegments ? (
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? '#ecf0f1' : 'text.primary',
                  lineHeight: 1.5,
                  fontSize: '0.8rem',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {transcription}
              </Typography>
            ) : (
              <Box>
                {segments.map((segment, index) => (
                  <Box key={segment.id} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme === 'dark' ? '#c084fc' : '#800080',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          minWidth: '60px',
                          fontFamily: 'monospace',
                        }}
                      >
                        [{segment.timecode}]
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDark ? '#ecf0f1' : 'text.primary',
                          fontSize: '0.75rem',
                          lineHeight: 1.4,
                          flexGrow: 1,
                        }}
                      >
                        {segment.text}
                      </Typography>
                    </Box>
                    {index < segments.length - 1 && (
                      <Divider sx={{ mt: 0.75, opacity: 0.3 }} />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          {/* Bouton Analyser avec GuardIA */}
          {false && !summary && (
            <>
              <Button
                variant="contained"
                onClick={handleSummarize}
                disabled={loadingSummary || !resumeAccess.hasAccess}
                startIcon={loadingSummary ? (
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                ) : (
                  <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <GuardiaLogo
                      style={{
                        width: '18px',
                        height: '18px',
                        color: 'white'
                      }}
                    />
                  </Box>
                )}
                sx={{
                  backgroundColor: theme === 'dark' ? '#c084fc' : '#800080',
                  color: 'white',
                  px: 2.5,
                  py: 0.75,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  mb: 1.5,
                  width: '100%',
                  '&:hover': {
                    backgroundColor: theme === 'dark' ? '#a855f7' : '#6b0050',
                  },
                  '&:disabled': {
                    backgroundColor: isDark ? '#2d3f4c' : 'grey.300',
                    color: isDark ? '#7f8c8d' : 'grey.500',
                  },
                }}
              >
                {loadingSummary ? 'Génération de l\'analyse...' : 'Analyser avec GuardIA'}
              </Button>
              {!resumeAccess.hasAccess && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.8 }}>
                  {resumeAccess.reason}
                </Typography>
              )}
            </>
          )}

          {/* Analyse GuardIA */}
          {summary && (
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                mb: 1.5,
                backgroundColor: isDark ? 'rgba(192, 132, 252, 0.1)' : 'rgba(128, 0, 128, 0.05)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${theme === 'dark' ? 'rgba(192, 132, 252, 0.3)' : 'rgba(128, 0, 128, 0.3)'}`,
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <GuardiaLogo
                    style={{
                      width: '20px',
                      height: '20px',
                      color: theme === 'dark' ? '#c084fc' : '#800080'
                    }}
                  />
                </Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: theme === 'dark' ? '#c084fc' : '#800080',
                    fontSize: '0.85rem',
                  }}
                >
                  Analysé par GuardIA
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: isDark ? '#ecf0f1' : 'text.primary',
                  lineHeight: 1.5,
                  fontSize: '0.8rem',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {summary}
              </Typography>

              <Button
                size="small"
                onClick={() => setSummary('')}
                sx={{
                  mt: 1,
                  color: isDark ? '#95a5a6' : 'text.secondary',
                  fontSize: '0.7rem',
                  px: 1,
                  py: 0.25,
                  minHeight: 'auto',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  },
                }}
              >
                Générer une nouvelle analyse
              </Button>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
}

export default GuardiaAudio;
